import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import Dropdown from "../common/Dropdown";
import { useCityOptions } from "../../hooks/useCityOptions";
import {
  Region,
  RegionFormData,
  RegionUpdateFormData,
  regionCreateSchema,
  regionUpdateSchema,
} from "../../schemas/regionSchema";

interface RegionFormProps {
  isOpen: boolean;
  initialData?: RegionFormData | RegionUpdateFormData | Region;
  onSubmit: (data: RegionFormData | RegionUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const RegionForm: React.FC<RegionFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    trigger,
  } = useForm<RegionFormData | RegionUpdateFormData>({
    resolver: zodResolver(isUpdate ? regionUpdateSchema : regionCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData
      ? {
          name: (initialData as Region).name || (initialData as RegionFormData).name || "",
          cityId: (initialData as Region).cityId || (initialData as RegionFormData).cityId || "",
        }
      : { name: "", cityId: "" },
  });

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          name: (initialData as Region).name || (initialData as RegionFormData).name || "",
          cityId: (initialData as Region).cityId || (initialData as RegionFormData).cityId || "",
        });
      } else {
        reset({
          name: "",
          cityId: "",
        });
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  const { cityOptions, isCityOptionsLoading } = useCityOptions();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={isUpdate ? "Edit Region" : "Create Region"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Dropdown
          label="City"
          options={cityOptions}
          value={watch("cityId")}
          name="cityId"
          onChange={(val) => setValue("cityId", val as string, { shouldValidate: false })}
          onBlur={() => trigger("cityId")}
          error={errors.cityId}
          disabled={isCityOptionsLoading}
        />

        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {isUpdate ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RegionForm;
